import { Component, inject, OnDestroy } from '@angular/core';
import { Validators } from '@angular/forms';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import {
	DadoEstatisticaMensal,
	DadoPrecipitacaoMensal,
} from '@modulos/home/submodulos/dados-meteorologicos/componentes/tabela-relatorio-pluv-mensal-personalizada/tabela-relatorio-pluv-mensal-personalizada.component';
import { Estacao } from '@home/submodulos/dados-meteorologicos/interfaces/filtros-opcoes';
import {
	DadoPrecipitacao,
	EstatisticasPrecipitacao,
	PostosRelatorios,
	RelatorioAnual,
	Relatorios,
	TipoEstatistica,
} from '@home/submodulos/dados-meteorologicos/interfaces/tabela-relatorio';
import {
	FormularioRelatorio,
	INPUTS_RELATORIOS,
	InstanciaRelatorio,
} from '@home/submodulos/dados-meteorologicos/interfaces/tipos-relatorios';
import { RelatoriosService } from '@home/submodulos/dados-meteorologicos/services/relatorios.service';
import { Select } from '@layout/interfaces/select';
import {
	alinharDado,
	baixarEAbrirPDF,
	getTracoIfNull,
	isNotNuloOuUndefined,
	numberToBrNumber,
	obter_erro_request,
	renderCoordenada,
} from '@utils';
import { DateTimeUtils } from '@utils/datetime-util';
import { DocumentExporter } from '@utils/document-exporter';
import * as pdfseira from '@utils/pdf-seira';
import { ToastrService } from 'ngx-toastr';
import { corrigeDuplicacaoNome } from '../../../utils';
import * as ExcelTable from 'mr-excel';
import { isNotNullOrUndefined } from '@angular-eslint/utils/dist/utils';
import { TipoTabela } from '../../../interfaces';

@Component({
	selector: 'seira-pluviometro-tabela-pluv-mensal-personalizada',
	templateUrl: './pluviometro-tabela-pluv-mensal-personalizada.component.html',
	styleUrls: ['./pluviometro-tabela-pluv-mensal-personalizada.component.scss'],
})
export class PluviometroTabelaPrecipitacaoMensalComponent
	implements InstanciaRelatorio, OnDestroy
{
	descricaoRelatorio =
		'Define-se como pluviometria mensal personalizada, o total pluviométrico acumulado no período de um mês.';

	postos!: PostosRelatorios[];
	estacoes: Estacao[] = [];
	microrregioes: Select[] = [];

	relatorio: Relatorios<DadoPrecipitacaoMensal>[] = [];
	estatisticas: Relatorios<DadoEstatisticaMensal>[] = [];

	opcoesColunas = [
		{ name: 'Latitude', isFixed: false },
		{ name: 'Longitude', isFixed: false },
		{ name: 'Altitude', isFixed: false },
		{ name: 'Microrregião', isFixed: false },
		{ name: 'Mesorregião', isFixed: false },
		{ name: 'Região Pluviométrica', isFixed: false },
		{ name: 'Bacia/Sub-bacia', isFixed: false },
	];
	colunasSelecionadas: string[] = [];
	exibirEstatisticas = true;

	inputs = inject(INPUTS_RELATORIOS);
	carregando = true;
	botoesDeExportacao: GroupButton[] = [
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: () => this.exportarPDF(),
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () => this.exportarCSV(),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () => this.exportarTXT(),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () => this.exportarXLSX(),
		},
	];
	tituloRelatorio = TipoTabela.PRECIPITACAO_MENSAL + ' (mm)';

	constructor(
		private readonly relatoriosService: RelatoriosService,
		private readonly toastr: ToastrService
	) {
		this.postos = this.inputs.postos;
		this.setValidators();
	}

	setColunasSelecionadas(colunas: string[]) {
		this.colunasSelecionadas = colunas;
	}

	alternarExibicaoEstatisticas(): void {
		this.exibirEstatisticas = !this.exibirEstatisticas;
	}

	ngOnDestroy(): void {
		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.POSTO)?.clearValidators();
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.POSTO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.setValidators(Validators.required);
	}

	private getPrecipitacaoAcumulada(
		dadoPrecipitacao: DadoPrecipitacao,
		index: number
	) {
		return dadoPrecipitacao.precipitacoesMensais?.[index]
			?.precipitacaoAcumulada;
	}

	private processarDadoEstatistica(
		estatisticas: EstatisticasPrecipitacao,
		tipoEstatistica: TipoEstatistica
	) {
		const labelEstatisticaMap: Record<string, string> = {
			anosContabilizados: 'Quantidade de anos',
			mediasMensais: 'Média (mm)',
			maximaMensal: 'Máxima (mm)',
			minimaMensal: 'Mínima (mm)',
		};

		return {
			estatistica: labelEstatisticaMap[tipoEstatistica],
			jan: estatisticas[tipoEstatistica][0],
			fev: estatisticas[tipoEstatistica][1],
			mar: estatisticas[tipoEstatistica][2],
			abr: estatisticas[tipoEstatistica][3],
			mai: estatisticas[tipoEstatistica][4],
			jun: estatisticas[tipoEstatistica][5],
			jul: estatisticas[tipoEstatistica][6],
			ago: estatisticas[tipoEstatistica][7],
			set: estatisticas[tipoEstatistica][8],
			out: estatisticas[tipoEstatistica][9],
			nov: estatisticas[tipoEstatistica][10],
			dez: estatisticas[tipoEstatistica][11],
		};
	}

	private processarDadoPrecipitacao(
		dadoPrecipitacao: DadoPrecipitacao,
		detalhesPosto?: any
	) {
		return {
			latitude: detalhesPosto?.latitude,
			longitude: detalhesPosto?.longitude,
			altitude: detalhesPosto?.altitude,
			microrregiao: detalhesPosto?.microrregiao || '-',
			mesorregiao: detalhesPosto?.mesorregiao || '-',
			regiao: detalhesPosto?.regiaoPluviometrica || detalhesPosto?.regiao || '-',
			bacia: detalhesPosto?.bacia || '-',
			ano: new Date(dadoPrecipitacao.ano).getFullYear(),
			jan: this.getPrecipitacaoAcumulada(dadoPrecipitacao, 0),
			fev: this.getPrecipitacaoAcumulada(dadoPrecipitacao, 1),
			mar: this.getPrecipitacaoAcumulada(dadoPrecipitacao, 2),
			abr: this.getPrecipitacaoAcumulada(dadoPrecipitacao, 3),
			mai: this.getPrecipitacaoAcumulada(dadoPrecipitacao, 4),
			jun: this.getPrecipitacaoAcumulada(dadoPrecipitacao, 5),
			jul: this.getPrecipitacaoAcumulada(dadoPrecipitacao, 6),
			ago: this.getPrecipitacaoAcumulada(dadoPrecipitacao, 7),
			set: this.getPrecipitacaoAcumulada(dadoPrecipitacao, 8),
			out: this.getPrecipitacaoAcumulada(dadoPrecipitacao, 9),
			nov: this.getPrecipitacaoAcumulada(dadoPrecipitacao, 10),
			dez: this.getPrecipitacaoAcumulada(dadoPrecipitacao, 11),
			total: dadoPrecipitacao.precipitacaoAcumuladaAno,
		};
	}

	private processarDadosEstatistica(
		estatisticasPrecipitacao: EstatisticasPrecipitacao
	) {
		return Object.keys(estatisticasPrecipitacao).map(tipoEstatistica =>
			this.processarDadoEstatistica(
				estatisticasPrecipitacao,
				tipoEstatistica as TipoEstatistica
			)
		);
	}

	private popularDadosTabela(relatorio: RelatorioAnual) {
		const municipio = relatorio.detalhesPosto.municipio;
		const nomePosto = relatorio.detalhesPosto.posto;
		const labelMunicipio = corrigeDuplicacaoNome(`${municipio}/${nomePosto}`);
		const detalhesPosto: any = relatorio.detalhesPosto;

		relatorio.dadosPrecipitacao.forEach(dadoPrecipitacao => {
			const dadoPrecipitacaoMensal = this.relatorio.find(
				dado => dado.municipio === labelMunicipio
			);
			const dadoEstatistica = this.estatisticas.find(
				dado => dado.municipio === labelMunicipio
			);
			const dadoPrecipitacaoMensalProcessado =
				this.processarDadoPrecipitacao(dadoPrecipitacao, detalhesPosto);
			const dadoEstatisticasProcessado = this.processarDadosEstatistica(
				relatorio.estatisticasPrecipitacao
			);

			if (dadoPrecipitacaoMensal && dadoEstatistica) {
				dadoPrecipitacaoMensal.data.push(dadoPrecipitacaoMensalProcessado);
				dadoEstatistica.data = dadoEstatisticasProcessado;
			} else {
				this.relatorio.push({
					municipio: labelMunicipio,
					data: [dadoPrecipitacaoMensalProcessado],
				});
				this.estatisticas.push({
					municipio: labelMunicipio,
					data: [...dadoEstatisticasProcessado],
				});
			}
		});
	}

	gerarRelatorio() {
		const dataInicio = this.getFormItemValue('dataInicio');
		const dataFim = this.getFormItemValue('dataFim');

		this.carregando = true;
		this.inputs.setLoading(true);

		const postosIds = this.postos.map(posto => posto.id);

		this.tituloRelatorio = `${
			TipoTabela.PRECIPITACAO_MENSAL
		} (mm) - ${DateTimeUtils.formatarDataPeriodo(
			this.getFormItemValue('dataInicio'),
			this.getFormItemValue('dataFim')
		)}`;

		this.relatoriosService
			.buscarRelatorioPrecipitacaoAnual(postosIds, dataInicio, dataFim)
			.subscribe({
				next: response => {
					this.relatorio = [];
					this.estatisticas = [];
					response.relatoriosAnuais.forEach(relatorio =>
						this.popularDadosTabela(relatorio)
					);
					this.relatoriosService.atualizaVisibilidadeBotoesExportacao(
						this.relatorio
					);
				},
				error: error => {
					this.carregando = false;
					const mensagem = obter_erro_request(error);
					this.toastr.error(mensagem, 'Ocorreu um erro ao gerar o relatório');
				},
				complete: () => {
					this.carregando = false;
					this.inputs.setLoading(false);
				},
			});
	}

	retornarPrecipitacaoMensalParaXLSX() {
		const tableData: any[] = [];

		this.relatorio.forEach(dadoRelatorio => {
			dadoRelatorio.data.forEach(dado => {
				const item: any = {
					municipioPosto: corrigeDuplicacaoNome(dadoRelatorio.municipio),
				};
				if (this.colunasSelecionadas.includes('Latitude')) {
					item.latitude = renderCoordenada(dado['latitude']?.toString());
				}
				if (this.colunasSelecionadas.includes('Longitude')) {
					item.longitude = renderCoordenada(dado['longitude']?.toString());
				}
				if (this.colunasSelecionadas.includes('Altitude')) {
					item.altitude = renderCoordenada(dado['altitude']?.toString(), 1);
				}
				if (this.colunasSelecionadas.includes('Microrregião')) {
					item.microrregiao = dado['microrregiao'] || '-';
				}
				if (this.colunasSelecionadas.includes('Mesorregião')) {
					item.mesorregiao = dado['mesorregiao'] || '-';
				}
				if (this.colunasSelecionadas.includes('Região Pluviométrica')) {
					item.regiao = dado['regiao'] || '-';
				}
				if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) {
					item.bacia = dado['bacia'] || '-';
				}
				item.ano = dado.ano;
				item.jan = getTracoIfNull(dado.jan);
				item.fev = getTracoIfNull(dado.fev);
				item.mar = getTracoIfNull(dado.mar);
				item.abr = getTracoIfNull(dado.abr);
				item.mai = getTracoIfNull(dado.mai);
				item.jun = getTracoIfNull(dado.jun);
				item.jul = getTracoIfNull(dado.jul);
				item.ago = getTracoIfNull(dado.ago);
				item.set = getTracoIfNull(dado.set);
				item.out = getTracoIfNull(dado.out);
				item.nov = getTracoIfNull(dado.nov);
				item.dez = getTracoIfNull(dado.dez);
				item.total = getTracoIfNull(dado.total);
				tableData.push(item);
			});
			if (this.exibirEstatisticas) {
				tableData.push({});
				tableData.push({
					municipioPosto: 'Estatísticas',
				});
				const estatisticasMunicipio = this.estatisticas.find(
					el => el.municipio === dadoRelatorio.municipio
				);
				estatisticasMunicipio?.data.forEach(e => {
					tableData.push({
						municipioPosto: e?.estatistica,
						jan: e?.jan ?? '',
						fev: e?.fev ?? '',
						mar: e?.mar ?? '',
						abr: e?.abr ?? '',
						mai: e?.mai ?? '',
						jun: e?.jun ?? '',
						jul: e?.jul ?? '',
						ago: e?.ago ?? '',
						set: e?.set ?? '',
						out: e?.out ?? '',
						nov: e?.nov ?? '',
						dez: e?.dez ?? '',
					});
				});
			}
			tableData.push({});
		});

		return tableData;
	}

	async exportarPDF() {
		const documentDefinition: any = await pdfseira.documentDefinitions(
			'landscape',
			'A3'
		);

		documentDefinition.content.push({
			text: this.tituloRelatorio,
			fontSize: 12,
			alignment: 'center',
			margin: [0, 10],
		});

		if (!this.relatorio.length) {
			documentDefinition.content.push({
				text: 'Nenhum dado encontrado na tabela',
				alignment: 'center',
				fontSize: 10,
				margin: [0, 10],
			});
		}

		const colunasMeses = [
			'Jan',
			'Fev',
			'Mar',
			'Abr',
			'Mai',
			'Jun',
			'Jul',
			'Ago',
			'Set',
			'Out',
			'Nov',
			'Dez',
		];

		const colunasPersonalizadasHeader = [];
		if (this.colunasSelecionadas.includes('Latitude')) colunasPersonalizadasHeader.push('Latitude');
		if (this.colunasSelecionadas.includes('Longitude')) colunasPersonalizadasHeader.push('Longitude');
		if (this.colunasSelecionadas.includes('Altitude')) colunasPersonalizadasHeader.push('Altitude');
		if (this.colunasSelecionadas.includes('Microrregião')) colunasPersonalizadasHeader.push('Microrregião');
		if (this.colunasSelecionadas.includes('Mesorregião')) colunasPersonalizadasHeader.push('Mesorregião');
		if (this.colunasSelecionadas.includes('Região Pluviométrica')) colunasPersonalizadasHeader.push('Região Pluviométrica');
		if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) colunasPersonalizadasHeader.push('Bacia/Sub-bacia');

		const headerPrecipitacao = [
			...colunasPersonalizadasHeader,
			'Data',
			...colunasMeses,
			'Total',
		];

		const numCols = headerPrecipitacao.length;
		let fontSize = 8.5;
		if (numCols > 18) {
			fontSize = 7.5;
		} else if (numCols > 15) {
			fontSize = 8;
		}

		const widthsPrecipitacao = headerPrecipitacao.map(colHeader => {
			const name = String(colHeader);
			if (name === 'Data') return 45;
			if (name === 'Total') return 40;
			if (name === 'Latitude') return 50;
			if (name === 'Longitude') return 50;
			if (name === 'Altitude') return 35;
			if (name === 'Microrregião') return 65;
			if (name === 'Mesorregião') return 65;
			if (name === 'Região Pluviométrica') return 70;
			if (name === 'Bacia/Sub-bacia') return 70;
			return '*';
		});

		let larguraAntesDosMeses = 45;
		if (this.colunasSelecionadas.includes('Latitude')) larguraAntesDosMeses += 50;
		if (this.colunasSelecionadas.includes('Longitude')) larguraAntesDosMeses += 50;
		if (this.colunasSelecionadas.includes('Altitude')) larguraAntesDosMeses += 35;
		if (this.colunasSelecionadas.includes('Microrregião')) larguraAntesDosMeses += 65;
		if (this.colunasSelecionadas.includes('Mesorregião')) larguraAntesDosMeses += 65;
		if (this.colunasSelecionadas.includes('Região Pluviométrica')) larguraAntesDosMeses += 70;
		if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) larguraAntesDosMeses += 70;

		const widthsEstatistica = [larguraAntesDosMeses, ...Array(12).fill('*'), 40];

		this.relatorio.forEach((dadoRelatorio, index) => {
			const precipitacoesAnuais = dadoRelatorio.data;
			const estatisticas = this.estatisticas[index]?.data || [];

			const dadosTabelaPrecipitacaoMensal: (string | any)[][] = [];

			dadosTabelaPrecipitacaoMensal.push(
				headerPrecipitacao.map(col => alinharDado(col, true))
			);

			precipitacoesAnuais.forEach(dado => {
				const colunasPersonalizadasLinha = [];
				if (this.colunasSelecionadas.includes('Latitude')) {
					colunasPersonalizadasLinha.push(alinharDado(renderCoordenada(dado['latitude']?.toString()), true));
				}
				if (this.colunasSelecionadas.includes('Longitude')) {
					colunasPersonalizadasLinha.push(alinharDado(renderCoordenada(dado['longitude']?.toString()), true));
				}
				if (this.colunasSelecionadas.includes('Altitude')) {
					colunasPersonalizadasLinha.push(alinharDado(renderCoordenada(dado['altitude']?.toString(), 1), true));
				}
				if (this.colunasSelecionadas.includes('Microrregião')) {
					colunasPersonalizadasLinha.push(alinharDado(dado['microrregiao']?.toString(), true));
				}
				if (this.colunasSelecionadas.includes('Mesorregião')) {
					colunasPersonalizadasLinha.push(alinharDado(dado['mesorregiao']?.toString(), true));
				}
				if (this.colunasSelecionadas.includes('Região Pluviométrica')) {
					colunasPersonalizadasLinha.push(alinharDado(dado['regiao']?.toString(), true));
				}
				if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) {
					colunasPersonalizadasLinha.push(alinharDado(dado['bacia']?.toString(), true));
				}

				const formatarMes = (d: any) =>
					isNotNuloOuUndefined(d)
						? alinharDado(String(numberToBrNumber(d, 1)), true)
						: alinharDado('-', true);

				dadosTabelaPrecipitacaoMensal.push([
					...colunasPersonalizadasLinha,
					alinharDado(dado.ano, true),
					formatarMes(dado.jan),
					formatarMes(dado.fev),
					formatarMes(dado.mar),
					formatarMes(dado.abr),
					formatarMes(dado.mai),
					formatarMes(dado.jun),
					formatarMes(dado.jul),
					formatarMes(dado.ago),
					formatarMes(dado.set),
					formatarMes(dado.out),
					formatarMes(dado.nov),
					formatarMes(dado.dez),
					formatarMes(dado.total),
				]);
			});

			const dadosTabelaEstatistica: (string | any)[][] = [];

			dadosTabelaEstatistica.push([
				alinharDado('Estatísticas', true),
				...colunasMeses.map(mes => alinharDado(mes, true)),
				alinharDado('', true),
			]);

			estatisticas.forEach(dado => {
				const formatarEst = (d: any) => {
					if (dado.estatistica === 'Quantidade de anos') {
						return alinharDado(d, true) || alinharDado(0, true);
					}
					return isNotNuloOuUndefined(d)
						? alinharDado(String(numberToBrNumber(d, 1)), true)
						: alinharDado('-', true);
				};

				dadosTabelaEstatistica.push([
					alinharDado(dado.estatistica, true),
					formatarEst(dado.jan),
					formatarEst(dado.fev),
					formatarEst(dado.mar),
					formatarEst(dado.abr),
					formatarEst(dado.mai),
					formatarEst(dado.jun),
					formatarEst(dado.jul),
					formatarEst(dado.ago),
					formatarEst(dado.set),
					formatarEst(dado.out),
					formatarEst(dado.nov),
					formatarEst(dado.dez),
					alinharDado('-', true),
				]);
			});

			documentDefinition.content.push(
				{
					text: dadoRelatorio.municipio,
					fontSize: 10,
					margin: [0, 20, 0, 5],
				},
				{
					table: {
						headerRows: 1,
						widths: widthsPrecipitacao,
						body: dadosTabelaPrecipitacaoMensal.map(row =>
							row.map(cell => ({
								text: cell,
								fontSize: fontSize,
								alignment: 'center',
							}))
						),
					},
					layout: {
						fillColor: (rowIndex: number) => (rowIndex === 0 ? '#DCDCDC' : null),
						paddingLeft: () => 2,
						paddingRight: () => 2,
						paddingTop: () => 2,
						paddingBottom: () => 2,
					},
				}
			);

			if (this.exibirEstatisticas) {
				documentDefinition.content.push({
					table: {
						headerRows: 1,
						widths: widthsEstatistica,
						body: dadosTabelaEstatistica.map(row =>
							row.map(cell => ({
								text: cell,
								fontSize: fontSize,
								alignment: 'center',
							}))
						),
					},
					layout: {
						fillColor: (rowIndex: number) => (rowIndex === 0 ? '#DCDCDC' : null),
						paddingLeft: () => 2,
						paddingRight: () => 2,
						paddingTop: () => 2,
						paddingBottom: () => 2,
					},
				});
			}
		});

		baixarEAbrirPDF(this.tituloRelatorio, documentDefinition);
	}

	async exportarCSV() {
		const tableData: (string | number)[][] = [];

		const colunasPersonalizadasHeader = [];
		if (this.colunasSelecionadas.includes('Latitude')) colunasPersonalizadasHeader.push('Latitude');
		if (this.colunasSelecionadas.includes('Longitude')) colunasPersonalizadasHeader.push('Longitude');
		if (this.colunasSelecionadas.includes('Altitude')) colunasPersonalizadasHeader.push('Altitude');
		if (this.colunasSelecionadas.includes('Microrregião')) colunasPersonalizadasHeader.push('Microrregião');
		if (this.colunasSelecionadas.includes('Mesorregião')) colunasPersonalizadasHeader.push('Mesorregião');
		if (this.colunasSelecionadas.includes('Região Pluviométrica')) colunasPersonalizadasHeader.push('Região Pluviométrica');
		if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) colunasPersonalizadasHeader.push('Bacia/Sub-bacia');

		tableData.push([
			'Município/Posto',
			...colunasPersonalizadasHeader,
			'Data',
			'Jan',
			'Fev',
			'Mar',
			'Abr',
			'Mai',
			'Jun',
			'Jul',
			'Ago',
			'Set',
			'Out',
			'Nov',
			'Dez',
			'Total',
		]);

		const formatar = (v: any) =>
			isNotNuloOuUndefined(v) ? Number(v).toFixed(1).replace('.', ',') : '-';

		this.relatorio.forEach(dadoRelatorio => {
			dadoRelatorio.data.forEach(dado => {
				const colunasPersonalizadasLinha: string[] = [];
				if (this.colunasSelecionadas.includes('Latitude')) {
					colunasPersonalizadasLinha.push(renderCoordenada(dado['latitude']?.toString()));
				}
				if (this.colunasSelecionadas.includes('Longitude')) {
					colunasPersonalizadasLinha.push(renderCoordenada(dado['longitude']?.toString()));
				}
				if (this.colunasSelecionadas.includes('Altitude')) {
					colunasPersonalizadasLinha.push(renderCoordenada(dado['altitude']?.toString(), 1));
				}
				if (this.colunasSelecionadas.includes('Microrregião')) {
					colunasPersonalizadasLinha.push(String(dado['microrregiao'] || '-'));
				}
				if (this.colunasSelecionadas.includes('Mesorregião')) {
					colunasPersonalizadasLinha.push(String(dado['mesorregiao'] || '-'));
				}
				if (this.colunasSelecionadas.includes('Região Pluviométrica')) {
					colunasPersonalizadasLinha.push(String(dado['regiao'] || '-'));
				}
				if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) {
					colunasPersonalizadasLinha.push(String(dado['bacia'] || '-'));
				}

				tableData.push([
					dadoRelatorio.municipio,
					...colunasPersonalizadasLinha,
					dado.ano,
					formatar(dado.jan),
					formatar(dado.fev),
					formatar(dado.mar),
					formatar(dado.abr),
					formatar(dado.mai),
					formatar(dado.jun),
					formatar(dado.jul),
					formatar(dado.ago),
					formatar(dado.set),
					formatar(dado.out),
					formatar(dado.nov),
					formatar(dado.dez),
					formatar(dado.total),
				]);
			});

			if (this.exibirEstatisticas) {
				tableData.push(['Estatísticas']);
				const estatisticasMunicipio = this.estatisticas.find(
					el => el.municipio === dadoRelatorio.municipio
				);
				const numColunasExtras = colunasPersonalizadasHeader.length + 1;
				estatisticasMunicipio?.data.forEach(e => {
					tableData.push([
						e.estatistica,
						...Array(numColunasExtras).fill(''),
						e?.jan != null ? (e.estatistica === 'Quantidade de anos' ? e.jan : formatar(e.jan)) : '',
						e?.fev != null ? (e.estatistica === 'Quantidade de anos' ? e.fev : formatar(e.fev)) : '',
						e?.mar != null ? (e.estatistica === 'Quantidade de anos' ? e.mar : formatar(e.mar)) : '',
						e?.abr != null ? (e.estatistica === 'Quantidade de anos' ? e.abr : formatar(e.abr)) : '',
						e?.mai != null ? (e.estatistica === 'Quantidade de anos' ? e.mai : formatar(e.mai)) : '',
						e?.jun != null ? (e.estatistica === 'Quantidade de anos' ? e.jun : formatar(e.jun)) : '',
						e?.jul != null ? (e.estatistica === 'Quantidade de anos' ? e.jul : formatar(e.jul)) : '',
						e?.ago != null ? (e.estatistica === 'Quantidade de anos' ? e.ago : formatar(e.ago)) : '',
						e?.set != null ? (e.estatistica === 'Quantidade de anos' ? e.set : formatar(e.set)) : '',
						e?.out != null ? (e.estatistica === 'Quantidade de anos' ? e.out : formatar(e.out)) : '',
						e?.nov != null ? (e.estatistica === 'Quantidade de anos' ? e.nov : formatar(e.nov)) : '',
						e?.dez != null ? (e.estatistica === 'Quantidade de anos' ? e.dez : formatar(e.dez)) : '',
					]);
				});
			}
		});

		DocumentExporter.gerarCSV(tableData, this.tituloRelatorio);
	}

	async exportarTXT() {
		const formatar = (v: any) =>
			isNotNuloOuUndefined(v) ? numberToBrNumber(v, 1) : '-';

		let txtData = '';

		this.relatorio.forEach(dadoRelatorio => {
			dadoRelatorio.data.forEach(dado => {
				txtData += `Município/Posto: ${dadoRelatorio.municipio}\n`;

				if (this.colunasSelecionadas.includes('Latitude')) {
					txtData += `Latitude: ${renderCoordenada(dado['latitude']?.toString())}\n`;
				}
				if (this.colunasSelecionadas.includes('Longitude')) {
					txtData += `Longitude: ${renderCoordenada(dado['longitude']?.toString())}\n`;
				}
				if (this.colunasSelecionadas.includes('Altitude')) {
					txtData += `Altitude: ${renderCoordenada(dado['altitude']?.toString(), 1)}\n`;
				}
				if (this.colunasSelecionadas.includes('Microrregião')) {
					txtData += `Microrregião: ${String(dado['microrregiao'] || '-')}\n`;
				}
				if (this.colunasSelecionadas.includes('Mesorregião')) {
					txtData += `Mesorregião: ${String(dado['mesorregiao'] || '-')}\n`;
				}
				if (this.colunasSelecionadas.includes('Região Pluviométrica')) {
					txtData += `Região Pluviométrica: ${String(dado['regiao'] || '-')}\n`;
				}
				if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) {
					txtData += `Bacia/Sub-bacia: ${String(dado['bacia'] || '-')}\n`;
				}

				txtData += `Data: ${dado.ano}\n`;
				txtData += `Jan: ${formatar(dado.jan)}\n`;
				txtData += `Fev: ${formatar(dado.fev)}\n`;
				txtData += `Mar: ${formatar(dado.mar)}\n`;
				txtData += `Abr: ${formatar(dado.abr)}\n`;
				txtData += `Mai: ${formatar(dado.mai)}\n`;
				txtData += `Jun: ${formatar(dado.jun)}\n`;
				txtData += `Jul: ${formatar(dado.jul)}\n`;
				txtData += `Ago: ${formatar(dado.ago)}\n`;
				txtData += `Set: ${formatar(dado.set)}\n`;
				txtData += `Out: ${formatar(dado.out)}\n`;
				txtData += `Nov: ${formatar(dado.nov)}\n`;
				txtData += `Dez: ${formatar(dado.dez)}\n`;
				txtData += `Total: ${formatar(dado.total)}\n`;

				if (this.exibirEstatisticas) {
					const { estatisticas } = this;
					txtData += '\nEstatísticas:';
					const estatisticasMunicipio = estatisticas.find(
						el => el.municipio === dadoRelatorio.municipio
					);
					estatisticasMunicipio?.data.forEach(e => {
						const formatarEst = (v: any) =>
							e.estatistica === 'Quantidade de anos' ? (v ?? '-') : formatar(v);

						txtData += `\n\n${e.estatistica}:\n`;
						txtData += `Jan: ${formatarEst(e.jan)}\n`;
						txtData += `Fev: ${formatarEst(e.fev)}\n`;
						txtData += `Mar: ${formatarEst(e.mar)}\n`;
						txtData += `Abr: ${formatarEst(e.abr)}\n`;
						txtData += `Mai: ${formatarEst(e.mai)}\n`;
						txtData += `Jun: ${formatarEst(e.jun)}\n`;
						txtData += `Jul: ${formatarEst(e.jul)}\n`;
						txtData += `Ago: ${formatarEst(e.ago)}\n`;
						txtData += `Set: ${formatarEst(e.set)}\n`;
						txtData += `Out: ${formatarEst(e.out)}\n`;
						txtData += `Nov: ${formatarEst(e.nov)}\n`;
						txtData += `Dez: ${formatarEst(e.dez)}`;
					});
				}

				txtData += '\n\n\n';
			});
		});

		DocumentExporter.gerarTXT(txtData, this.tituloRelatorio);
	}

	exportarXLSX() {
		const nomeArquivo = this.tituloRelatorio;
		const colorPalette = {
			c1: '2C3639',
			c2: 'FFFFFF',
			c3: '000000',
			c4: 'EEEEEE',
		};
		const rowStyle = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const rowStyleDecimal1 = {
			...rowStyle,
			format: 'num_sep_1',
		};
		const rowStyleInteger = {
			...rowStyle,
			format: 'num_sep',
		};
		const rowStyleLeft = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
			alignment: {
				horizontal: 'left',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const headerStyle = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
		};
		const headerStyleCenter = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const title = {
			backgroundColor: colorPalette.c2,
			whiteSpace: 'pre',
			color: colorPalette.c3,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
				wrapText: 1,
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const headers: any[] = [
			{ label: 'municipioPosto', text: 'Município / Posto', size: 24 }
		];
		if (this.colunasSelecionadas.includes('Latitude')) {
			headers.push({ label: 'latitude', text: 'Latitude', size: 15 });
		}
		if (this.colunasSelecionadas.includes('Longitude')) {
			headers.push({ label: 'longitude', text: 'Longitude', size: 15 });
		}
		if (this.colunasSelecionadas.includes('Altitude')) {
			headers.push({ label: 'altitude', text: 'Altitude', size: 15 });
		}
		if (this.colunasSelecionadas.includes('Microrregião')) {
			headers.push({ label: 'microrregiao', text: 'Microrregião', size: 20 });
		}
		if (this.colunasSelecionadas.includes('Mesorregião')) {
			headers.push({ label: 'mesorregiao', text: 'Mesorregião', size: 20 });
		}
		if (this.colunasSelecionadas.includes('Região Pluviométrica')) {
			headers.push({ label: 'regiao', text: 'Região Pluviométrica', size: 20 });
		}
		if (this.colunasSelecionadas.includes('Bacia/Sub-bacia')) {
			headers.push({ label: 'bacia', text: 'Bacia/Sub-bacia', size: 20 });
		}
		headers.push(
			{ label: 'ano', text: 'Data', size: 24 },
			{ label: 'jan', text: 'Jan', size: 12 },
			{ label: 'fev', text: 'Fev', size: 12 },
			{ label: 'mar', text: 'Mar', size: 12 },
			{ label: 'abr', text: 'Abr', size: 12 },
			{ label: 'mai', text: 'Mai', size: 12 },
			{ label: 'jun', text: 'Jun', size: 12 },
			{ label: 'jul', text: 'Jul', size: 12 },
			{ label: 'ago', text: 'Ago', size: 12 },
			{ label: 'set', text: 'Set', size: 12 },
			{ label: 'out', text: 'Out', size: 12 },
			{ label: 'nov', text: 'Nov', size: 12 },
			{ label: 'dez', text: 'Dez', size: 12 },
			{ label: 'total', text: 'Total', size: 12 }
		);

		const dataExcel = {
			styles: {
				headerStyle,
				headerStyleCenter,
				rowStyle,
				rowStyleDecimal1,
				rowStyleInteger,
				title,
				rowStyleLeft,
			},
			sheet: [
				{
					shiftTop: 3,
					images: [
						{
							url: 'assets/images/cabecalho/cabeçalho_excel.png',
							from: 'A1',
							to: 'C3',
						},
					],
					styleCellCondition: (
						data: any,
						object: any,
						rowIndex: number,
						colIndex: number
					) => {
						if (data && data.label) {
							return 'headerStyleCenter';
						}

						const label = headers[colIndex]?.label;
						if (
							label === 'municipioPosto' ||
							label === 'microrregiao' ||
							label === 'mesorregiao' ||
							label === 'regiao' ||
							label === 'bacia'
						) {
							return 'rowStyleLeft';
						}

						if (label === 'ano') {
							return 'rowStyleInteger';
						}

						const valor = object?.[label];
						return typeof valor === 'number' ? 'rowStyleDecimal1' : 'rowStyle';
					},
					headers: headers,
					data: this.retornarPrecipitacaoMensalParaXLSX(),
					convertStringToNumber: true,
					columns: [],
					title: {
						consommeRow: 3,
						consommeCol: 3,
						text: nomeArquivo,
						styleId: 'title',
					},
				},
			],
			fileName: nomeArquivo,
		};

		ExcelTable.generateExcel(dataExcel);
	}

	getFormItemValue(controlName: string): any {
		return this.inputs.form.get(controlName)!.value;
	}
}
